import { BusinessSchema, paths } from '@acs/shared';
import type { Firestore } from 'firebase/firestore/lite';
import type { BusinessDoc } from './converters.js';
import { app } from './firebase.js';

/**
 * One-shot reads that go straight to the server, bypassing the live SDK.
 *
 * The portal's data arrives through `onSnapshot` listeners on a persistent,
 * multi-tab cache — and that is exactly the machinery that can go quiet
 * without saying so: a background tab holding the shared connection gets
 * frozen by the browser, a phone suspends the page while the owner makes a
 * call, a laptop wakes with a stream that neither errors nor delivers. Even the
 * full SDK's `getDocFromServer` rides the same listener plumbing, so it waits
 * on the same dead stream.
 *
 * The Lite SDK is a plain request per read — no listeners, no cache, no tab
 * leadership — so it answers whenever the network does. Kept for the few
 * places where the owner is staring at the screen waiting for one specific
 * server-side change (the forwarding check); everything else stays on the
 * live listeners.
 *
 * Loaded on the first read, not at import: its own chunk, fetched only by a
 * screen that actually polls — and test doubles of `./firebase.js` never
 * reach it.
 */
type LiteSdk = typeof import('firebase/firestore/lite');
let lite: Promise<{ sdk: LiteSdk; db: Firestore }> | null = null;

function liteDb(): Promise<{ sdk: LiteSdk; db: Firestore }> {
  if (!lite) {
    lite = import('firebase/firestore/lite').then((sdk) => {
      const db = sdk.getFirestore(app);
      if (import.meta.env.VITE_USE_EMULATORS === 'true') sdk.connectFirestoreEmulator(db, 'localhost', 8080);
      return { sdk, db };
    });
    // A failed chunk load must not poison every later read.
    lite.catch(() => {
      lite = null;
    });
  }
  return lite;
}

/**
 * The business document as the server holds it right now, parsed like the live
 * converter parses it. Null when it is gone or does not parse — a caller that
 * only patches a cache must not replace a good copy with a half-read one.
 */
export async function fetchBusinessFromServer(businessId: string): Promise<BusinessDoc | null> {
  const { sdk, db } = await liteDb();
  const snap = await sdk.getDoc(sdk.doc(db, paths.business(businessId)));
  if (!snap.exists()) return null;
  const parsed = BusinessSchema.safeParse(snap.data());
  return parsed.success ? { ...parsed.data, id: snap.id } : null;
}
